import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { api } from "@/lib/api";
import { sizedImg } from "@/lib/images";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, MoveHorizontal } from "lucide-react";

export default function Progetti() {
  const [projects, setProjects] = useState([]);
  const [stateFilter, setStateFilter] = useState("tutti");
  const [filter, setFilter] = useState("Tutti");

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  }, []);

  const totalCount = projects.length;
  const completedCount = projects.filter((p) => !p.wip).length;
  const wipCount = projects.filter((p) => p.wip).length;

  const byState = projects.filter((p) => {
    if (stateFilter === "completati") return !p.wip;
    if (stateFilter === "wip") return p.wip;
    return true;
  });

  const categories = ["Tutti", ...Array.from(new Set(byState.map((p) => p.category)))];
  const visible = filter === "Tutti" ? byState : byState.filter((p) => p.category === filter);

  // Reset category if no longer available after state switch
  useEffect(() => {
    if (filter !== "Tutti" && !categories.includes(filter)) setFilter("Tutti");
  }, [stateFilter, projects, categories, filter]);

  const states = [
    { key: "tutti", label: "Tutti", count: totalCount },
    { key: "completati", label: "Completati", count: completedCount },
    { key: "wip", label: "Work in progress", count: wipCount },
  ];

  return (
    <div data-testid="progetti-page">
      <PageHeader
        index="/ 05"
        title="Progetti"
        lines={["Alcuni dei nostri", "cantieri."]}
        subtitle="Una selezione dei lavori realizzati in Puglia e in tutta Italia. Ogni progetto è un banco di prova per la nostra tecnica."
        logo
      />

      <section className="pb-24 md:pb-32 bg-background">
        <div className="max-w-[1500px] mx-auto px-5 md:px-10">
          <Reveal>
            <div className="space-y-4 mb-12">
              {/* State filter */}
              <div className="flex flex-wrap items-center gap-3">
                <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mr-1">// Stato</span>
                {states.map((s) => (
                  <button
                    key={s.key}
                    data-testid={`state-filter-${s.key}`}
                    onClick={() => setStateFilter(s.key)}
                    className={`inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest px-4 py-2.5 border transition-colors ${
                      stateFilter === s.key ? "bg-foreground text-background border-foreground" : "border-border hover:border-foreground"
                    }`}
                  >
                    {s.label}
                    <span
                      className={`inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 font-mono text-[10px] tabular-nums ${
                        stateFilter === s.key ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"
                      }`}
                      data-testid={`state-count-${s.key}`}
                    >
                      {s.count}
                    </span>
                  </button>
                ))}
              </div>

              {/* Category filter */}
              <div className="flex flex-wrap items-center gap-3">
                <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mr-1">// Categoria</span>
                {categories.map((c) => (
                  <button
                    key={c}
                    data-testid={`filter-${c.replace(/\s/g, "-")}`}
                    onClick={() => setFilter(c)}
                    className={`font-mono text-xs uppercase tracking-widest px-4 py-2.5 border transition-colors ${
                      filter === c ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-foreground"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-6">
            <AnimatePresence mode="popLayout">
              {visible.map((p, i) => (
                <motion.div
                  key={p.id}
                  layout
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5, delay: (i % 2) * 0.08 }}
                  data-testid="project-card"
                >
                  <Link
                    to={`/progetti/${p.slug || p.id}`}
                    data-testid={`project-card-link-${p.slug || p.id}`}
                    className="group relative flex flex-col h-full overflow-hidden border border-border bg-card"
                  >
                    <div className="relative aspect-[16/10] overflow-hidden">
                      <img src={sizedImg(p.image, 900)} alt={p.title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      <div className="absolute inset-0 bg-foreground/40 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      <span className="absolute top-4 left-4 font-mono text-xs bg-primary text-primary-foreground px-3 py-1.5 uppercase tracking-widest">{p.category}</span>
                      {p.wip && (
                        <span data-testid={`project-wip-${p.slug}`} className="absolute top-4 right-4 font-mono text-xs bg-foreground text-background px-3 py-1.5 uppercase tracking-widest">
                          Work in Progress
                        </span>
                      )}
                      {p.before_after && p.before_after.before && p.before_after.after && (
                        <span
                          data-testid={`project-ba-badge-${p.slug}`}
                          className="absolute bottom-4 left-4 inline-flex items-center gap-1.5 font-mono text-[10px] md:text-xs bg-background/95 backdrop-blur-sm text-foreground px-3 py-1.5 uppercase tracking-widest border border-foreground shadow-[0_4px_12px_rgba(0,0,0,0.25)]"
                          title="Confronta cantiere e risultato finale"
                        >
                          <MoveHorizontal className="w-3.5 h-3.5" />
                          Prima / Dopo
                        </span>
                      )}
                    </div>
                    <div className="p-7 flex flex-col flex-1">
                      <h3 className="font-head font-bold text-2xl md:text-3xl tracking-tight">{p.title}</h3>
                      <p className="font-mono text-xs text-muted-foreground mt-2 uppercase tracking-widest">{p.location} — {p.year}</p>
                      <p className="text-muted-foreground mt-4 leading-relaxed flex-1">{p.description}</p>
                      <span className="mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest group-hover:text-primary group-hover:gap-3 transition-all">
                        Vedi il cantiere <ArrowUpRight className="w-4 h-4" />
                      </span>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {visible.length === 0 && (
            <p className="font-mono text-sm text-muted-foreground py-20 text-center">Nessun progetto corrisponde ai filtri selezionati.</p>
          )}
        </div>
      </section>
    </div>
  );
}
